import React, { FC, useCallback, useMemo } from "react";
import ReactFlow, {
  addEdge,
  ConnectionLineType,
  Controls,
  MiniMap,
  useEdgesState,
  useNodesState,
  Node,
  Edge,
  Position,
  Background,
} from "reactflow";
import dagre from "dagre";
import "reactflow/dist/style.css";

import { JobApplicationStatus, UserRole } from "../../types/api";
import Modal from "../ui/Modal";

interface ApplicationStatusFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onStageSelect: (stage: JobApplicationStatus) => void;
  currentStatus: string;
  userRole?: UserRole;
}

const statusGraph: Record<JobApplicationStatus, JobApplicationStatus[]> = {
  Invited: ["Bookmarked", "ViewedByCandidate"],
  Bookmarked: ["NotInterested", "Applied"],
  ViewedByCandidate: ["NotInterested", "Applied"],
  NotInterested: [],
  Applied: ["WithdrawnByCandidate", "ViewedByEmployer"],
  WithdrawnByCandidate: [],
  ViewedByEmployer: ["UnderReview", "Rejected"],
  UnderReview: ["Shortlisted", "Rejected"],
  Shortlisted: ["ReachedOut", "Rejected"],
  ReachedOut: ["InterviewScheduled"],
  InterviewScheduled: ["InterviewCompleted", "InterviewNo_Show"],
  InterviewNo_Show: ["InterviewCompleted", "Rejected"],
  InterviewCompleted: ["Offered", "Rejected"],
  Rejected: [],
  Offered: ["OfferedAccepted", "OfferDeclined"],
  OfferedAccepted: ["Hired", "OnHold", "Waitlisted"],
  OfferDeclined: [],
  Hired: [],
  OnHold: [],
  Waitlisted: [],
};

const companyAllowedStatus: JobApplicationStatus[] = [
  "ViewedByEmployer",
  "UnderReview",
  "Shortlisted",
  "ReachedOut",
  "InterviewScheduled",
  "InterviewNo_Show",
  "InterviewCompleted",
  "Rejected",
  "Offered",
  "Hired",
  "OnHold",
  "Waitlisted",
];

const statusColors: { [key: string]: string } = {
  InterviewNo_Show: "red-300",
  Rejected: "red-300",
  Hired: "green-100",
  OnHold: "yellow-100",
  Waitlisted: "yellow-100",
};

const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 172;
const nodeHeight = 36;

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  dagreGraph.setGraph({ rankdir: "TB" });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  nodes.forEach((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    node.targetPosition = Position.Top;
    node.sourcePosition = Position.Bottom;

    // We are shifting the dagre node position (anchor=center center) to the top left
    // so it matches the React Flow node anchor point (top left).
    node.position = {
      x: nodeWithPosition.x - nodeWidth / 2,
      y: nodeWithPosition.y - nodeHeight / 2,
    };

    return node;
  });

  return { nodes, edges };
};

const ApplicationStatusFlow: FC<ApplicationStatusFlowProps> = ({
  isOpen,
  onClose,
  onStageSelect,
  currentStatus,
  userRole,
}) => {
  const isStatusDisabled = (status: JobApplicationStatus) => {
    if (userRole === "Company") {
      return !companyAllowedStatus.includes(status);
    }
    return false;
  };
  
  const initialEdges = useMemo(() => {
    const edges: Edge[] = [];
    Object.keys(statusGraph).forEach((source) => {
      statusGraph[source as JobApplicationStatus].forEach((target) => {
        edges.push({
          id: `${source}-${target}`,
          source: source,
          target: target,
          type: "smoothstep",
          animated: true,
        });
      });
    });
    return edges;
  }, []);

  interface StatusNode extends Node {disabled:boolean, status : JobApplicationStatus}
  
  const initialNodes = useMemo(() => {
    const nodes: StatusNode[] = [];
    Object.keys(statusGraph).forEach((status) => {
      const disabled = isStatusDisabled(status as JobApplicationStatus);
      nodes.push({
        id: status,
        data: { label: status },
        position: { x: 0, y: 0 }, // Initial position, will be updated by layout
        style: {
          background:
            status === currentStatus
              ? `#${statusColors[status] || "909090"}`
              : `#${statusColors[status] || "ffffff"}`,
          color: status === currentStatus ? "white" : "black",
          border: "1px solid #000",
          width: 180,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? "not-allowed" : "pointer",
        },
        draggable: false,
        disabled,
        status: status as JobApplicationStatus
        // focusable: status === currentStatus
      });
    });
    const isNodeReachableFromCurrent = (currentStatus: JobApplicationStatus,node : JobApplicationStatus) => {
      if (currentStatus == node) return true;
      const edges = initialEdges.filter(edge => edge.source == currentStatus)
      const childrenNodes = edges.map(edge => nodes.find(node => node.id == edge.target && !node.disabled))
      if (childrenNodes.length > 0){
        return childrenNodes.reduce((acc,targetNode)=>{
          if (targetNode == undefined || targetNode.disabled) return acc
          return acc || isNodeReachableFromCurrent(targetNode.status, node)
        },false)
      }
      return false;
    }
    nodes.forEach(node => {
      if (!node.disabled && userRole === "Company" ) {
        const isDisabled = !isNodeReachableFromCurrent(currentStatus as JobApplicationStatus ,node.status)
        node.disabled = isDisabled;
        if (node.style){
          node.style.opacity = isDisabled ? 0.5 : 1;
          node.style.cursor =  isDisabled ? "not-allowed" : "pointer";
        }
      } 
      return node
    })
    return nodes;
  }, [currentStatus]);

  

  const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
    initialNodes,
    initialEdges
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

  const onConnect = useCallback(
    (params: any) =>
      setEdges((eds) =>
        addEdge({ ...params, type: ConnectionLineType.SmoothStep, animated: true }, eds)
      ),
    [setEdges]
  );

  const handleNodeClick = (event: React.MouseEvent, node: Node) => {
    const status = node.id as JobApplicationStatus;
    if (!isStatusDisabled(status)) {
      onStageSelect(status);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Application Status"
      size="full"
    >
      <div style={{ height: "80vh" }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={handleNodeClick}
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
        >
          <MiniMap />
          <Controls />
          <Background />
        </ReactFlow>
      </div>
    </Modal>
  );
};

export default ApplicationStatusFlow;
